/**
 * 一覧の条件。検索 Input(常時)+ 保存ビューのチップ列 + 条件チップ + 「条件を追加」。
 *
 * 「絞り込む」ボタンは持たない。検索語は親が `useDebounced` で落ち着かせてから
 * URL に載せ、条件は選んだ瞬間に効く。条件の値も URL に載せる(`urlState.ts`)。
 *
 * - IME の変換中は親に流さない(b1 fix2 低 3)。変換中の確定前文字で URL が書き換わり、
 *   再描画で変換が切れていた
 * - 吹き出しの焦点移動は**条件ごと**の ref で(b1 fix2 低 1)。1 本の ref だと
 *   2 つ目以降の条件で最初の入力に焦点が移らなかった
 * - `sort` を渡すと右端に「並び順」の Select を出す
 */
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input, Popover, Select } from "antd";
import { useRef, useState, type CompositionEvent, type ReactNode } from "react";

export type ViewChip = {
  key: string;
  label: string;
  count?: number;
  /** 件数の札を目立たせる(要確認など) */
  tone?: "warning";
};

export type ConditionPreset = {
  label: string;
  /** 押したときに条件を置き換える(呼び出し側が URL に patch する) */
  patch: () => void;
};

export type ConditionDef = {
  key: string;
  label: string;
  /** いまの値の表示。空なら未適用(チップを出さず「条件を追加」の中に入る) */
  value?: string;
  /** 値を選ぶ UI。`close` で吹き出しを閉じる */
  control: (close: () => void) => ReactNode;
  onClear: () => void;
  /** 吹き出しの上に並ぶ早見(「今週」「期限超過」など) */
  preset?: ConditionPreset[];
};

export type SortOption = { value: string; label: string };

export function FilterBar({
  search,
  views,
  conditions = [],
  sort,
  right,
}: {
  /** `onChange` に `cancel()` があれば(`useSearchParamInput` の setter)、IME の変換開始で予約を取り消す */
  search: { value: string; onChange: ((v: string) => void) & { cancel?: () => void }; placeholder: string };
  views?: { items: ViewChip[]; active: string; onChange: (key: string) => void };
  conditions?: ConditionDef[];
  /** 並び順(右端の Select) */
  sort?: { value: string; options: SortOption[]; onChange: (v: string) => void };
  /** 右端(件数や表示の切替) */
  right?: ReactNode;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const close = () => setEditing(null);
  const pops = useRef<Record<string, HTMLDivElement | null>>({});
  // 吹き出しの中の最初の入力に焦点を移す。**位置が決まってから**(`afterOpenChange`)、
  // ページをスクロールさせずに(`preventScroll`)。control 側の `autoFocus` は位置決め前に
  // 走ってページが 200px ほど飛んでいた(fix2 のスクショで発覚)
  const focusFirst = (key: string) => (open: boolean) => {
    if (!open) return;
    pops.current[key]
      ?.querySelector<HTMLElement>('input:not([type="hidden"]), button, [tabindex]:not([tabindex="-1"])')
      ?.focus({ preventScroll: true });
  };
  const inactive = conditions.filter((c) => !c.value);

  // IME 変換中は手元で持ち、確定(compositionend)でまとめて親へ
  const composing = useRef(false);
  const [draft, setDraft] = useState<string | null>(null);
  const onCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
    composing.current = false;
    setDraft(null);
    search.onChange(e.currentTarget.value);
  };

  return (
    <div className="ui-filterbar">
      {views && views.items.length > 0 && (
        <div className="ui-views" role="tablist" aria-label="ビュー">
          {views.items.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              className="ui-view"
              aria-selected={views.active === v.key}
              onClick={() => views.onChange(v.key)}
            >
              {v.label}
              {v.count != null && (
                <span className={`ui-view-count${v.tone === "warning" ? " is-warning" : ""}`}>
                  {v.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="ui-filter" style={{ marginTop: views ? 12 : 0 }}>
        <Input
          className="ui-filter-search"
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--ux-text-tertiary)" }} />}
          placeholder={search.placeholder}
          value={draft ?? search.value}
          onChange={(e) => {
            if (composing.current) setDraft(e.target.value);
            else {
              setDraft(null);
              search.onChange(e.target.value);
            }
          }}
          onCompositionStart={() => {
            composing.current = true;
            // 変換前の入力で予約した書込みを取り消す(変換中に URL と一覧を書き換えない。fix1 低1)。
            // 確定値は compositionend で予約し直す
            search.onChange.cancel?.();
            setDraft(search.value);
          }}
          onCompositionEnd={onCompositionEnd}
          aria-label="検索"
        />
        {conditions
          .filter((c) => c.value || editing === c.key)
          .map((c) => (
            <Popover
              key={c.key}
              open={editing === c.key}
              onOpenChange={(o) => setEditing(o ? c.key : null)}
              trigger="click"
              placement="bottomLeft"
              afterOpenChange={focusFirst(c.key)}
              content={
                <div
                  className="ui-filter-pop"
                  ref={(el) => {
                    pops.current[c.key] = el;
                  }}
                >
                  {c.preset && c.preset.length > 0 && (
                    <div className="ui-filter-presets">
                      {c.preset.map((p) => (
                        <Button
                          key={p.label}
                          size="small"
                          onClick={() => {
                            p.patch();
                            close();
                          }}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  {c.control(close)}
                </div>
              }
            >
              <span className="ui-chip">
                <button type="button" className="ui-chip-label" onClick={() => setEditing(c.key)}>
                  <b>{c.label}: </b>
                  {c.value || "選択中…"}
                </button>
                <button
                  type="button"
                  className="ui-chip-x"
                  aria-label={`${c.label}の条件を外す`}
                  onClick={() => {
                    close();
                    c.onClear();
                  }}
                >
                  <CloseOutlined />
                </button>
              </span>
            </Popover>
          ))}
        {inactive.length > 0 && (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: inactive.map((c) => ({ key: c.key, label: c.label })),
              onClick: ({ key }) => setEditing(String(key)),
            }}
          >
            <button type="button" className="ui-chip-add">
              + 条件を追加
            </button>
          </Dropdown>
        )}
        <span className="ui-filter-spacer" />
        {sort && (
          <label className="ui-filter-sort">
            並び順
            <Select
              size="small"
              value={sort.value}
              options={sort.options}
              onChange={sort.onChange}
              popupMatchSelectWidth={false}
              aria-label="並び順"
            />
          </label>
        )}
        {right && <div className="ui-filter-right">{right}</div>}
      </div>
    </div>
  );
}

/** 吹き出しの中の「閉じる」。control の末尾に置く。 */
export function PopClose({ onClick }: { onClick: () => void }) {
  return (
    <Button size="small" type="primary" onClick={onClick} style={{ alignSelf: "flex-end" }}>
      閉じる
    </Button>
  );
}
